import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch, API } from '../api';
import PickingWindow from '../PickingWindow';
import { setTrackContext, flushTracking } from '../pp/track';
import PinPad from './PinPad';
import { guidedText } from './guidedText';
import AnnouncementBar from './AnnouncementBar';
import TabletTrip, { BetweenRounds, SessionView } from '../inbound/TabletTrip';
import { ReadyInbound, TripView } from '../inbound/inboundApi';

// The cart's tablet in a guided round (phase 4, 2026-10-17; docs/GUIDED-PP.md). The tablet is fixed on its cart and
// has no scanner. Fernando (19.10): "Identify yourself. Please enter your PIN." — the PIN says who it is (no list of
// rounds or names), and the WMS opens the round: the one already on this cart, their own waiting round, or the only
// round waiting (a takeover, recorded); people with the right choose when several wait. The walk is the familiar
// picking screen, with the plan the SERVER froze for this round and cart; every step stores the position on the server.
// 1.11 (Fernando: "Tablet accounts in general should only have the rights to accept cart related tasks"): the tablet
// also takes an inbound REPLENISH TRIP — above the PIN pad a card per inbound with boxes ready at the inbound station;
// tap → PIN → the trip (inbound/TabletTrip). A trip on this cart takes the screen until it is back (no picking round
// starts on a cart that carries inbound boxes). The picking path itself is unchanged. -- Claude

interface RoundView {
  id: number; phase: string; person: string | null; name: string | null; station_no: number | null;
  orders: number; started_at: string; position: { phase: string; load: number; stop: number } | null; stops: number | null;
  paused_reason?: string | null;
}
interface TabletState {
  tablet: string; cart: { id: string; name: string; boxes: number } | null;
  my_round: RoundView | null; waiting: RoundView[];
}
interface Walk {
  round: RoundView; plan: any; position: any; order_ids: string[]; person: string; name: string; lang: string;
}

const C = {
  bg: 'bg-[#e5e7eb]', text: 'text-[#111111]', muted: 'text-[#5f6368]',
  card: 'bg-white border border-[#9aa0a6] rounded-2xl',
};

// the inbound part of the home screen, in the tablet's language (the person is not known before the PIN)
const RT = {
  en: { head: 'Replenish — inbounds', title: (w: string) => `Inbound ${w}`, ready: (b: number, p: number, s: string) => `${b} box${b === 1 ? '' : 'es'} · ${p} pcs ready at ${s}`,
        waiting: (n: number) => `${n} for waiting customers`, pinFor: (w: string) => `Inbound ${w} — enter your PIN`,
        onCart: (n: number, w: string, who: string) => `Trip ${n} of inbound ${w} is on this cart (${who})`, cont: 'Enter the PIN to continue',
        paused: (who: string) => `Replenishing paused — ${who}`, contPin: 'Continue (PIN)', end: 'End replenishing' },
  et: { head: 'Riiulisse — sissetulekud', title: (w: string) => `Sissetulek ${w}`, ready: (b: number, p: number, s: string) => `${b} kasti · ${p} tk valmis: ${s}`,
        waiting: (n: number) => `${n} ootavatele klientidele`, pinFor: (w: string) => `Sissetulek ${w} — sisesta oma PIN`,
        onCart: (n: number, w: string, who: string) => `Sissetuleku ${w} käik ${n} on selles kärus (${who})`, cont: 'Jätkamiseks sisesta PIN',
        paused: (who: string) => `Riiulisse panek pausil — ${who}`, contPin: 'Jätka (PIN)', end: 'Lõpeta riiulisse panek' },
  ru: { head: 'Раскладка — поставки', title: (w: string) => `Поставка ${w}`, ready: (b: number, p: number, s: string) => `${b} коробок · ${p} шт готово: ${s}`,
        waiting: (n: number) => `${n} для ждущих клиентов`, pinFor: (w: string) => `Поставка ${w} — введите PIN`,
        onCart: (n: number, w: string, who: string) => `Рейс ${n} поставки ${w} на этой тележке (${who})`, cont: 'Введите PIN, чтобы продолжить',
        paused: (who: string) => `Раскладка на паузе — ${who}`, contPin: 'Продолжить (PIN)', end: 'Закончить раскладку' },
};

async function detail(r: Response) {
  const d = await r.json().catch(() => ({} as any));
  return typeof d.detail === 'string' ? d.detail : `HTTP ${r.status}`;
}

export default function CartApp() {
  const [st, setSt] = useState<TabletState | null>(null);
  const [view, setView] = useState<'idle' | 'choose' | 'walk' | 'done' | 'tripPin' | 'trip'>('idle');
  const [inb, setInb] = useState<ReadyInbound[]>([]);                   // inbounds with boxes ready (1.11)
  const [myTrip, setMyTrip] = useState<TripView | null>(null);          // an inbound trip on this cart
  const [session, setSession] = useState<SessionView | null>(null);     // a replenishing session on this cart (2.11)
  const viewRef = useRef(view);
  viewRef.current = view;
  const [tripView, setTripView] = useState<TripView | null>(null);
  const [pinFor, setPinFor] = useState<{ inboundId: number | null; wgr: string } | null>(null);
  const [choices, setChoices] = useState<RoundView[]>([]);
  const [walk, setWalk] = useState<Walk | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState('en');
  const pinRef = useRef('');                      // only between "which round?" and the tap — never stored
  const last = useRef<{ phase: string; load: number; stop: number } | null>(null);
  // orders that left the round while it is being picked (cancelled by the shop, removed by a manager)
  const [dontPick, setDontPick] = useState<{ order_number: string; items: { location: string; product_code: string; qty: number }[] }[]>([]);
  useEffect(() => {
    if (view !== 'walk' || !walk) { setDontPick([]); return; }
    const get = () => authFetch(`${API}/pp2/rounds/${walk.round.id}/alerts`).then(r => r.ok ? r.json() : null)
      .then(d => d && setDontPick(d.dont_pick || [])).catch(() => {});
    get();
    const t = setInterval(get, 15000);
    return () => clearInterval(t);
  }, [view, walk]);
  const tx = guidedText(lang);

  const cantFind = async (p: { location: string; product_code: string; qty: number; picklist: string }) => {
    if (!walk) return '';
    const r = await authFetch(`${API}/pp2/rounds/${walk.round.id}/cant-find`, { method: 'POST', body: JSON.stringify(p) });
    if (!r.ok) throw new Error(await detail(r));
    const d = await r.json();
    const who = (d.short || []).map((s: any) => s.order_number).join(', ');
    const box = d.in_box ? ` ${tx.inBoxHint(d.in_box.order_number || '?')}` : '';
    // the order is not moved: the managers are told now, the packer decides at packing (Fernando 21.10)
    return (who ? tx.cantFindReported(p.picklist, who) : tx.cantFindReported(p.picklist, '')) + box;
  };

  const load = useCallback(() => {
    authFetch(`${API}/inbound/tablet/ready`).then(async r => {
      if (!r.ok) return;                                              // the inbound part is extra: picking never waits on it
      const j = await r.json(); setInb(j.ready || []); setMyTrip(j.my_trip || null); setSession(j.session || null);
      // the session goes on (2.11): its round on the cart opens without a PIN (a reload, the inbound page and back)
      if (j.session && !j.session.paused && j.my_trip && viewRef.current === 'idle') { setTripView(j.my_trip); setView('trip'); }
    }).catch(() => {});
    return authFetch(`${API}/pp2/tablet/state`).then(async r => {
      if (!r.ok) { setErr(await detail(r)); return; }
      setSt(await r.json());
    }).catch(() => setErr('No connection to the WMS'));
  }, []);
  useEffect(() => {
    if (view !== 'idle' && view !== 'choose') return;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [view, load]);

  const identify = async (pin: string, roundId?: number) => {
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/pp2/tablet/identify`, {
        method: 'POST', body: JSON.stringify({ pin, ...(roundId ? { round_id: roundId } : {}) }) });
      if (!r.ok) { setErr(await detail(r)); setView('idle'); pinRef.current = ''; return; }
      const d = await r.json();
      if (d.action === 'choose') { pinRef.current = pin; setChoices(d.rounds || []); setView('choose'); return; }
      pinRef.current = '';
      const w: Walk = d;
      setTrackContext({ roundId: w.round.id, actor: w.person, lang: w.lang, flowKey: 'guided' });
      setLang(w.lang || 'en');
      last.current = w.position || null;
      setWalk(w); setView('walk');
    } catch { setErr('No connection to the WMS'); } finally { setBusy(false); }
  };

  const onStep = useCallback((p: { phase: string; load: number; stop: number }) => {
    last.current = p;
    if (!walk) return;
    authFetch(`${API}/pp2/rounds/${walk.round.id}/position`, { method: 'POST', body: JSON.stringify(p) }).catch(() => {});
  }, [walk]);

  const onClose = async () => {
    if (!walk) { setView('idle'); return; }
    flushTracking();
    if (last.current?.phase === 'done') {
      const r = await authFetch(`${API}/pp2/rounds/${walk.round.id}/picked`, { method: 'POST' });
      if (r.ok) { setView('done'); setWalk(null); return; }
      setErr(await detail(r));
    }
    setWalk(null); setView('idle');
  };

  /** an inbound trip: the PIN on this tablet → the trip (a new one of that inbound, or the one already on this cart) */
  const acceptTrip = async (pin: string) => {
    if (!pinFor) return;
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/inbound/tablet/accept`, {
        method: 'POST', body: JSON.stringify({ pin, ...(pinFor.inboundId ? { inbound_id: pinFor.inboundId } : {}) }) });
      if (!r.ok) { setErr(await detail(r)); return; }
      const d: any = await r.json();
      if (d.action === 'session') { setPinFor(null); setView('idle'); load(); return; }     // the session continues between rounds
      setTripView(d); setPinFor(null); setView('trip');
    } catch { setErr('No connection to the WMS'); } finally { setBusy(false); }
  };
  const acceptTripNow = async (pin: string) => {
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/inbound/tablet/accept`, { method: 'POST', body: JSON.stringify({ pin }) });
      if (!r.ok) { setErr(await detail(r)); return; }
      const d: TripView = await r.json();
      setTripView(d); setPinFor(null); setView('trip');
    } catch { setErr('No connection to the WMS'); } finally { setBusy(false); }
  };
  const tripHome = () => { setTripView(null); setView('idle'); setErr(''); load(); };

  const onPause = async () => {
    if (!walk) return;
    await authFetch(`${API}/pp2/rounds/${walk.round.id}/pause`, { method: 'POST', body: JSON.stringify({ reason: 'break' }) }).catch(() => {});
    setWalk(null); setView('idle');
  };

  if (view === 'walk' && walk) {
    return (
      <>
        <PickingWindow orderIds={walk.order_ids} presetPlan={walk.plan} startAt={walk.position}
          roundId={walk.round.id} picker={walk.person} cartId={st?.cart?.id || null}
          onStep={onStep} onPause={onPause} onClose={onClose} onCantFind={cantFind} />
        {dontPick.length > 0 && (
          <div className="fixed top-0 inset-x-0 z-[70] bg-[#b91c1c] text-white px-5 py-3 text-lg font-semibold shadow-lg">
            {dontPick.map(o => (
              <div key={o.order_number}>Order {o.order_number} was cancelled — do NOT pick: {o.items.map(i => `${i.location || '?'} (${i.product_code}) ×${i.qty}`).join(', ')}</div>
            ))}
          </div>
        )}
      </>
    );
  }

  if (view === 'trip' && tripView) {
    return <TabletTrip view={tripView} onView={setTripView} onPaused={tripHome} onClose={tripHome} />;
  }
  // an open session between rounds (2.11): no PIN pad — Start a new round, Pause, Hand over, End replenishing
  if (view === 'idle' && session && !session.paused && !myTrip) {
    return <BetweenRounds key={session.id + session.person} session={session} ready={inb} lastInbound={session.last_inbound}
      onTrip={v => { setTripView(v); setView('trip'); }} onPaused={tripHome} onEnded={tripHome} />;
  }
  const rt = RT[(lang as 'en' | 'et' | 'ru')] || RT.en;

  const onCart = st?.my_round;
  return (
    <div className={`fixed inset-0 ${C.bg} ${C.text} flex flex-col overflow-y-auto`}>
      <div className="flex-none border-b border-[#c8ccd2] bg-[#d9dce1] px-5 py-3 flex items-center gap-3">
        <span className="font-bold text-lg">{st?.cart ? st.cart.name : 'Cart'}</span>
        <span className={`text-sm ${C.muted}`}>{st?.tablet}</span>
      </div>
      <div className="flex-1 px-5 py-6 max-w-3xl w-full mx-auto">
        {view === 'done' && (
          <div className="text-center py-16 space-y-4">
            <div className="text-4xl font-black">{tx.doneTitle}</div>
            <div className="text-xl">{tx.doneSub}</div>
            <button onClick={() => setView('idle')} className="mt-6 px-8 py-4 rounded-2xl bg-[#111111] text-white text-xl font-bold">OK</button>
          </div>
        )}

        {view === 'choose' && (
          <div className="space-y-3">
            <div className="text-2xl font-bold mb-2">{tx.chooseRoundTitle}</div>
            {choices.map(r => (
              <button key={r.id} disabled={busy} onClick={() => identify(pinRef.current, r.id)}
                className={`${C.card} w-full text-left p-4 active:bg-[#f3f4f6]`}>
                <div className="text-3xl font-black">{tx.roundOf(r.name || r.person || '?', r.orders)}</div>
                <div className={`text-lg ${C.muted}`}>#{r.id}</div>
              </button>
            ))}
            <button onClick={() => { pinRef.current = ''; setView('idle'); }} className={`${C.card} px-6 py-3 text-lg mt-4`}>{tx.cancel}</button>
          </div>
        )}

        {view === 'tripPin' && pinFor && (
          <PinPad title={rt.pinFor(pinFor.wgr)} error={err} busy={busy} onSubmit={pin => acceptTrip(pin)} cancelLabel={tx.cancel}
            onCancel={() => { setPinFor(null); setErr(''); setView('idle'); }} />
        )}

        {view === 'idle' && myTrip && (
          <div className="space-y-6">
            <AnnouncementBar />
            <div className={`${C.card} p-4`}>
              <div className="text-2xl font-black">{rt.onCart(myTrip.trip.trip_no, myTrip.inbound.wgr_order_id || String(myTrip.inbound.id), myTrip.trip.name)}</div>
              <div className={`text-lg ${C.muted} mt-1`}>{myTrip.trip.paused ? tx.pausedTitle : rt.cont}</div>
            </div>
            <PinPad title={rt.cont} error={err} busy={busy} cancelLabel={tx.cancel} onCancel={() => setErr('')}
              onSubmit={pin => { setPinFor({ inboundId: null, wgr: myTrip.inbound.wgr_order_id || String(myTrip.inbound.id) }); acceptTripNow(pin); }} />
          </div>
        )}

        {view === 'idle' && !myTrip && (
          <div className="space-y-6">
            <AnnouncementBar />
            {onCart && (
              <div className={`${C.card} p-4`}>
                <div className="text-2xl font-black">{tx.yourRound(onCart.id)}</div>
                <div className={`text-lg ${C.muted} mt-1`}>{onCart.name} · {tx.phase[onCart.phase] || onCart.phase}</div>
              </div>
            )}
            {session && session.paused && (
              <div className={`${C.card} p-4 flex flex-wrap items-center gap-3`}>
                <div className="text-2xl font-black flex-1">{rt.paused(session.name || session.person)}</div>
                <button disabled={busy} onClick={() => { setErr(''); setPinFor({ inboundId: null, wgr: session.last_inbound?.wgr_order_id || '' }); setView('tripPin'); }}
                  className="px-5 py-3 rounded-xl bg-[#111111] text-white text-lg font-bold">{rt.contPin}</button>
                <button disabled={busy} onClick={() => authFetch(`${API}/inbound/tablet/end`, { method: 'POST' }).then(() => load())}
                  className={`text-base underline ${C.muted}`}>{rt.end}</button>
              </div>
            )}
            {!onCart && inb.length > 0 && (
              <div className="space-y-2">
                <div className={`text-sm uppercase tracking-wider ${C.muted}`}>{rt.head}</div>
                {inb.map(r => (
                  <button key={r.inbound_id} disabled={busy} onClick={() => { setErr(''); setPinFor({ inboundId: r.inbound_id, wgr: r.wgr_order_id || String(r.inbound_id) }); setView('tripPin'); }}
                    className={`${C.card} w-full text-left p-4 active:bg-[#f3f4f6]`}>
                    <div className="text-2xl font-black">{rt.title(r.wgr_order_id || String(r.inbound_id))} <span className={`text-lg font-normal ${C.muted}`}>{r.supplier_name}</span></div>
                    <div className="text-lg">{rt.ready(r.boxes, r.pieces, r.station)}{r.special.length ? ` · ${r.special.join(', ')}` : ''}</div>
                    {r.waiting > 0 && <div className="text-lg font-semibold text-sky-800">{rt.waiting(r.waiting)}</div>}
                  </button>
                ))}
              </div>
            )}
            <PinPad title={onCart?.phase === 'paused' ? tx.pausedTitle : tx.identifyTablet} error={err} busy={busy}
              onSubmit={pin => identify(pin)} cancelLabel={tx.cancel} onCancel={() => setErr('')} />
            {!onCart && st && <div className={`text-center text-lg ${C.muted}`}>
              {st.waiting.length ? tx.waitingInfo(st.waiting.length) : tx.noWaiting}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
